class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.jpOverlayClickHandler = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.jpOverlayClickHandler);
    this.jpSectionLoadHandler = this.setHeaderCartIconAccessibility.bind(this);
    this.jpOpenToken = 0;
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    document.addEventListener('shopify:section:load', this.jpSectionLoadHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.jpSectionLoadHandler);
  }

  setHeaderCartIconAccessibility() {
    // JP EDIT: every branded cart affordance opens Dawn's accessible drawer;
    // its href remains a complete no-JS fallback.
    const cartLinks = document.querySelectorAll('#cart-icon-bubble, [data-jp-cart-drawer-trigger]');
    cartLinks.forEach((cartLink) => {
      if (cartLink.__jpCartDrawerBound) return;
      cartLink.__jpCartDrawerBound = true;
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      const openDrawer = (event) => {
        event.preventDefault();
        let focusTarget = cartLink;
        if (cartLink.closest('[data-jp-layer="drawer"]')) {
          focusTarget = document.querySelector('[data-jp-open="drawer"]') || cartLink;
          window.JapanScissors?.layers?.close('drawer');
        }
        this.open(focusTarget);
      };
      cartLink.addEventListener('click', openDrawer);
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') openDrawer(event);
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const wasActive = this.classList.contains('active');
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    const token = ++this.jpOpenToken;
    const inner = this.querySelector('.drawer__inner');

    if (this.jpTransitionElement && this.jpTransitionHandler) {
      this.jpTransitionElement.removeEventListener('transitionend', this.jpTransitionHandler);
    }
    window.clearTimeout(this.jpFocusTrapTimer);

    let focusTrapActivated = false;
    const activateFocusTrap = () => {
      if (focusTrapActivated || token !== this.jpOpenToken || !this.classList.contains('active')) return;
      /* Trap the actual dialog in both empty and populated states. The former
         empty-state container was a child of the focus target, so reduced-
         motion browsers could install a trap while leaving focus on the
         opener outside it. Prefer the close control, with the focusable dialog
         itself as a resilient fallback. */
      const containerToTrapFocusOn = this.querySelector('.drawer__inner');
      const focusElement = containerToTrapFocusOn?.querySelector('.drawer__close') || containerToTrapFocusOn;
      if (!containerToTrapFocusOn || !focusElement) return;
      trapFocus(containerToTrapFocusOn, focusElement);
      /* Do not consume the transition fallback until focus really entered the
         dialog. Immediately after the active class is applied, some mobile
         browsers still regard the transitioning drawer as non-focusable. */
      if (document.activeElement !== containerToTrapFocusOn && !containerToTrapFocusOn.contains(document.activeElement)) {
        return;
      }
      focusTrapActivated = true;
      if (this.jpTransitionElement && this.jpTransitionHandler) {
        this.jpTransitionElement.removeEventListener('transitionend', this.jpTransitionHandler);
      }
      window.clearTimeout(this.jpFocusTrapTimer);
    };
    const activateFocusTrapAfterLayout = () => {
      requestAnimationFrame(() => requestAnimationFrame(activateFocusTrap));
    };

    this.jpTransitionElement = inner;
    this.jpTransitionHandler = (event) => {
      if (event.target === inner && event.propertyName === 'transform') activateFocusTrap();
    };
    inner?.addEventListener('transitionend', this.jpTransitionHandler);

    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      if (token !== this.jpOpenToken) return;
      this.classList.add('animate', 'active');
      const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      if (wasActive || reducedMotion) activateFocusTrapAfterLayout();
      this.jpFocusTrapTimer = window.setTimeout(() => {
        activateFocusTrap();
        if (!focusTrapActivated) activateFocusTrapAfterLayout();
      }, 450);
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.jpOpenToken += 1;
    window.clearTimeout(this.jpFocusTrapTimer);
    if (this.jpTransitionElement && this.jpTransitionHandler) {
      this.jpTransitionElement.removeEventListener('transitionend', this.jpTransitionHandler);
    }
    this.classList.remove('active');
    removeTrapFocus(this.activeElement || null);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.replaceRenderedSections(parsedState, true);
  }

  reconcileContents(parsedState) {
    this.replaceRenderedSections(parsedState, false);
  }

  replaceRenderedSections(parsedState, shouldOpen) {
    this.productId = parsedState.id;
    const drawerMarkup = parsedState.sections?.['cart-drawer'];
    const sourceDrawer = drawerMarkup
      ? new DOMParser().parseFromString(drawerMarkup, 'text/html').querySelector('cart-drawer')
      : null;
    if (sourceDrawer) {
      this.classList.toggle(
        'is-empty',
        sourceDrawer.classList.contains('is-empty') || Boolean(sourceDrawer.querySelector('.drawer__inner-empty'))
      );
    }
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      const replacement = this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector);
      if (replacement !== null) sectionElement.innerHTML = replacement;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.jpOverlayClickHandler);
      if (shouldOpen) this.open();
    });
    document.dispatchEvent(new CustomEvent('jp:cart-dom-updated', {
      detail: { source: shouldOpen ? 'product-form' : 'product-form-error-reconcile' },
    }));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;
    const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
